import React, { useCallback } from "react";

import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, CircularProgress, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { serializeError } from "@/utils/errors";

import Dialog from "./Dialog";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  applyLabel?: string;
  cancelLabel?: string;
}

const useStyles = makeStyles()({
  button: {
    "& .MuiButton-label": {
      textTransform: "none",
    },
  },
});

const validationSchema = yup
  .object({
    promotionCode: yup.string().label("Coupon code").required(),
  })
  .defined();

const applyCouponCodeMutation = graphql(`
  mutation ApplyPromotionCode($promotionCode: String!) {
    applyPromotionCode(input: { promotionCode: $promotionCode }) {
      coach {
        id
      }
    }
  }
`);

const CouponCodeDialog: React.FC<Props> = ({
  open,
  onClose,
  title = "Apply coupon code",
  applyLabel = "Apply",
  cancelLabel = "Cancel",
}) => {
  const { classes } = useStyles();
  const [applyPromotionCode] = useMutation(applyCouponCodeMutation);
  const snackbar = useGlobalSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm({
    defaultValues: {
      promotionCode: "",
    },
    resolver: yupResolver(validationSchema),
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose]);

  const onSubmit = useCallback(async (values: { promotionCode: string }) => {
    try {
      await applyPromotionCode({
        variables: { promotionCode: values.promotionCode },
      });
      snackbar.openSnackBar("Coupon code applied successfully");
      handleClose();
    } catch (error) {
      const err = serializeError(error);
      setError("promotionCode", { message: err.message });
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={title}
      content={
        <TextField
          {...register("promotionCode")}
          placeholder="Coupon code"
          error={!!errors.promotionCode}
          helperText={errors.promotionCode?.message}
          fullWidth
          autoFocus
        />
      }
      actions={
        <>
          <Button className={classes.button} onClick={handleSubmit(onSubmit)}>
            {isSubmitting ? (
              <CircularProgress color="inherit" size={24} />
            ) : (
              applyLabel
            )}
          </Button>
          <Button
            className={classes.button}
            variant="outlined"
            onClick={handleClose}
          >
            {cancelLabel}
          </Button>
        </>
      }
    />
  );
};

export default CouponCodeDialog;
